import React from "react"
import { useTheme } from '../../context/ThemeContext';
import { 
  ChartBarIcon, 
  UserGroupIcon, 
  GlobeAltIcon, 
  RocketLaunchIcon,
  ShieldCheckIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

const team = [
  {
    name: 'Ujjal Das',
    role: 'Founder & CEO',
    bio: 'A visionary leader with over 10 years of experience in financial technology. Passionate about democratizing access to market data and empowering traders with cutting-edge tools.',
    imageUrl: 'https://images.unsplash.com/photo-1667058014960-b4d3c76047a2?q=80&w=580&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    linkedin: '#',
  },
];

const milestones = [
  {
    year: '2024',
    title: 'Company Founded',
    description: 'Started the journey to democratize access to financial market data and empower traders worldwide.',
  },
  {
    year: '2025',
    title: 'AWS Collaboration',
    description: 'Formed strategic partnership with AWS to leverage cloud infrastructure for scalable market data solutions.',
  },
  {
    year: '2025',
    title: 'MVP Launch',
    description: 'Successfully launched our Minimum Viable Product, providing real-time market data access to early adopters.',
  },
];

export const Company: React.FC = () => {
  const { isDarkMode } = useTheme();
  
  return (
    <div className={`${isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}`}>
      {/* Hero Section */}
      <div className="relative isolate overflow-hidden">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8">
          <div className="mx-auto max-w-2xl lg:mx-0">
            <h1 className="text-4xl font-bold tracking-tight sm:text-6xl">
              Our Vision for the Future of Trading
            </h1>
            <p className={`mt-6 text-lg leading-8 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              We're building a future where everyone has access to professional-grade market data and trading tools. Our mission is to empower traders and investors with the information they need to make informed decisions in today's fast-paced markets.
            </p>
          </div>
        </div>
        <svg
          viewBox="0 0 1024 1024"
          className="absolute left-1/2 top-1/2 -z-10 h-[64rem] w-[64rem] -translate-x-1/2 -translate-y-1/2 [mask-image:radial-gradient(closest-side,white,transparent)]"
          aria-hidden="true"
        >
          <circle cx="512" cy="512" r="512" fill="url(#gradient)" fillOpacity="0.7" />
          <defs>
            <radialGradient id="gradient">
              <stop stopColor={isDarkMode ? '#3b82f6' : '#7775D6'} />
              <stop offset="1" stopColor={isDarkMode ? '#1e40af' : '#E935C1'} />
            </radialGradient>
          </defs>
        </svg>
      </div>

      {/* Team Section */}
      <div className={`mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
        <div className="mx-auto max-w-2xl lg:mx-0">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">Meet Our Leadership</h2>
          <p className={`mt-6 text-lg leading-8 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Our team brings together expertise in finance, technology, and data science to build the future of trading.
          </p>
        </div>
        <div className="mx-auto mt-20 max-w-2xl lg:max-w-none">
          <div className="flex justify-center">
            {team.map((person) => (
              <div key={person.name} className={`w-full max-w-md rounded-2xl p-8 shadow-md ${isDarkMode ? 'bg-gray-700' : 'bg-white'}`}>
                <div className="flex flex-col items-center">
                  <img 
                    className="h-48 w-48 rounded-full object-cover" 
                    src={person.imageUrl} 
                    alt={person.name} 
                  />
                  <h3 className={`mt-6 text-xl font-semibold leading-8 tracking-tight text-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {person.name}
                  </h3>
                  <p className="text-base leading-7 text-accent-primary text-center">{person.role}</p>
                  <p className={`mt-4 text-sm leading-6 text-center ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {person.bio}
                  </p>
                  <ul role="list" className="mt-6 flex gap-x-6">
                    <li>
                      <a href={person.linkedin} className="text-gray-400 hover:text-accent-primary">
                        <span className="sr-only">LinkedIn</span>
                        <svg className="h-5 w-5" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Timeline Section */}
      <div className="mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8">
        <div className="mx-auto max-w-2xl lg:mx-0">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">Our Journey</h2>
          <p className={`mt-6 text-lg leading-8 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            From our founding vision to today's industry leadership, see how we've grown while staying true to our mission.
          </p>
        </div>
        <div className="mx-auto mt-16 max-w-2xl lg:max-w-none">
          <div className="relative">
            {/* Vertical line */}
            <div className={`absolute left-4 top-0 h-full w-0.5 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}></div>
            
            {/* Milestones */}
            <div className="space-y-12">
              {milestones.map((item, index) => (
                <div key={item.year} className="relative pl-12">
                  {/* Circle marker */}
                  <div className={`absolute left-0 top-1 h-8 w-8 rounded-full border-4 ${isDarkMode ? 'border-accent-primary bg-gray-800' : 'border-accent-primary bg-white'}`}>
                    <div className="flex h-full w-full items-center justify-center">
                      <span className="text-sm font-semibold text-accent-primary">{item.year}</span>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className={`rounded-lg p-6 shadow-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                    <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {item.title}
                    </h3>
                    <p className={`mt-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className={`mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
        <div className="mx-auto max-w-2xl rounded-3xl ring-1 ring-gray-200 dark:ring-gray-700 sm:mt-20 lg:mx-0 lg:flex lg:max-w-none">
          <div className="p-8 sm:p-10 lg:flex-auto">
            <h3 className="text-2xl font-bold tracking-tight">Join Our Mission</h3>
            <p className={`mt-6 text-base leading-7 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Be part of a team that's shaping the future of trading. We're always looking for talented individuals who share our vision and values.
            </p>
            <div className="mt-10 flex items-center gap-x-4">
              <h4 className={`flex-none text-sm font-semibold leading-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Why Join Us</h4>
              <div className="h-px flex-auto bg-gray-100 dark:bg-gray-700" />
            </div>
            <ul
              role="list"
              className={`mt-8 grid grid-cols-1 gap-4 text-sm leading-6 sm:grid-cols-2 sm:gap-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}
            >
              <li className="flex gap-x-3">
                <svg className="h-6 w-5 flex-none text-accent-primary" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                </svg>
                Work with industry experts
              </li>
              <li className="flex gap-x-3">
                <svg className="h-6 w-5 flex-none text-accent-primary" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                </svg>
                Shape the future of trading
              </li>
              <li className="flex gap-x-3">
                <svg className="h-6 w-5 flex-none text-accent-primary" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                </svg>
                Continuous learning and growth
              </li>
              <li className="flex gap-x-3">
                <svg className="h-6 w-5 flex-none text-accent-primary" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                </svg>
                Competitive benefits package
              </li>
            </ul>
          </div>
          <div className="mt-10 p-2 lg:mt-0 lg:w-full lg:max-w-md lg:flex-shrink-0">
            <div className="rounded-2xl py-10 text-center ring-1 ring-inset ring-gray-900/5 lg:flex lg:flex-col lg:justify-center lg:py-16">
              <div className="mx-auto max-w-xs px-8">
                <p className="text-base font-semibold text-gray-600 dark:text-gray-400">Open Positions</p>
                <p className="mt-6 flex items-baseline justify-center gap-x-2">
                  <span className="text-5xl font-bold tracking-tight text-gray-900 dark:text-white">5+</span>
                  <span className="text-sm font-semibold leading-6 text-gray-600 dark:text-gray-400">roles available</span>
                </p>
                <a
                  href="#/careers"
                  className="mt-10 block w-full rounded-md bg-accent-primary px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-accent-primary-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-primary"
                >
                  View Open Positions
                </a>
                <p className="mt-6 text-xs leading-5 text-gray-600 dark:text-gray-400">
                  Join our team and help build the future of trading
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
  